/**
 * The flat attribute-filter dialect: negation as an `inverse` boolean inside
 * the block, and `value` always present.
 *
 * The other dialect — `attribute-envelope.ts` — spells negation as a
 * `{ not: … }` wrapper and omits `value` for a unary operator. Both describe
 * the same condition, and the difference is not cosmetic: the campaign trigger
 * wire's producer writes `inverse` onto every leaf, so a codec that omitted the
 * key would rewrite every condition it round-tripped.
 *
 * Shared rather than per-surface because the two campaign trigger wires
 * (`campaign.trigger` and `campaign.relSegAttrTrigger`) are the same wire
 * family through the same encoder. The operator vocabulary stays per-surface:
 * each plugin declares its own families and gates its claim on them.
 */

import { isRecord } from '@/utils/objects';
import { isTwoValueFilterOperator, isUnaryFilterOperator, type AttributeFilterState } from './attribute-filter-state';
import { isSupportedOperator, type GroupableFamily } from './operator-groups';
import { timestampVariantOf } from './operators';
import { SECONDS_IN_DAY } from './relative-timestamp';

/** The block of a flat attribute leaf, as it appears on the wire. */
export type FlatWireAttributeFilter = {
  field: string;
  operator: string;
  value: string | null;
  inverse: boolean;
  value2?: string | null;
};

/**
 * A flat attribute block as it comes off the wire, before any claim.
 *
 * Every field is `unknown`, because a stored block is editable by other
 * producers: the shape checks below decide what is actually there.
 */
export type WireAttributeBlock = {
  field?: unknown;
  operator?: unknown;
  value?: unknown;
  value2?: unknown;
  inverse?: unknown;
  /**
   * The three keys the compare envelope in `codecs/flat-comparison-filter.ts`
   * adds on top of the leaf. Declared here because both readers check the same
   * stored block and the key-exhaustive claim gate has to see every key a
   * producer may have written, whichever dialect it is in.
   */
  type?: unknown;
  field_source?: unknown;
  value_source?: unknown;
};

/** The keys the flat block models. Anything else declines the claim. */
const FLAT_FILTER_KEYS: ReadonlySet<string> = new Set(['field', 'operator', 'value', 'value2', 'inverse']);

/**
 * The block's operator in row-state spelling, where an `inverse` block reads
 * as the `!`-prefixed operator the row models.
 */
function flatUiOperator(block: WireAttributeBlock): string {
  const operator = String(block.operator ?? '');
  return block.inverse === true ? `!${operator}` : operator;
}

/**
 * Whether the block has the flat leaf's base shape with no key outside the
 * given set.
 *
 * The key-exhaustive rule matters on the claiming side: a block carrying a key
 * this codec does not model would lose that key on the next save, where
 * declining the claim keeps the stored bytes intact. The set is a parameter
 * because the compare envelope in `codecs/flat-comparison-filter.ts` is this
 * same base shape with three more modelled keys — one shape check for both, so
 * they cannot drift on what a flat leaf minimally is.
 */
export function flatAttributeBlockShape(
  block: unknown,
  allowedKeys: ReadonlySet<string> = FLAT_FILTER_KEYS,
): WireAttributeBlock | null {
  if (!isRecord(block)) {
    return null;
  }
  const record: WireAttributeBlock = block;
  if (typeof record.field !== 'string' || typeof record.operator !== 'string') {
    return null;
  }
  if (record.inverse !== undefined && typeof record.inverse !== 'boolean') {
    return null;
  }
  return Object.keys(block).every((key) => allowedKeys.has(key)) ? record : null;
}

/** The flat leaf's own modelled keys, as the attribute codec below reads them. */
function flatBlockShape(block: unknown): WireAttributeBlock | null {
  return flatAttributeBlockShape(block, FLAT_FILTER_KEYS);
}

/**
 * Whether the block is a flat attribute leaf this surface's families claim.
 *
 * The value checks are the arity rules: a value on a unary operator, or a
 * second value on an operator that takes one, is a shape this codec cannot
 * round-trip, so it declines rather than dropping the extra on save.
 *
 * `one_of` is declined unless the caller opts in, even when its families list
 * it. Most surfaces render no `one_of` value editor, so for them the operator
 * stays out of the vocabulary rather than being claimed onto a row that
 * cannot edit it.
 */
export function isSupportedFlatAttributeFilter(
  block: unknown,
  families: readonly GroupableFamily[],
  allowOneOfOperators = false,
): boolean {
  const record = flatBlockShape(block);
  if (record == null) {
    return false;
  }
  const operator = flatUiOperator(record);
  if (!isSupportedOperator(operator, families, allowOneOfOperators)) {
    return false;
  }
  if (!isUnaryFilterOperator(operator) && record.value != null && typeof record.value !== 'string') {
    return false;
  }
  if (isTwoValueFilterOperator(operator)) {
    return record.value2 == null || typeof record.value2 === 'string';
  }
  return record.value2 == null;
}

/**
 * The row state for a flat attribute block. Call only behind
 * `isSupportedFlatAttributeFilter`: a throw escaping a plugin's `parse` is a
 * plugin bug under dryad's contract, so a plugin declines with `null` first.
 *
 * `allowedKeys` is how the compare envelope reuses this reader: the four leaf
 * fields are read the same way whichever dialect carries them, and only the set
 * of keys that count as modelled differs. Deciding that is the claim gate's
 * job — by the time a block reaches here it has already been accepted — so
 * widening the set here keeps one reader for the leaf instead of a second copy
 * that could disagree about what `inverse` or a unary value means.
 */
export function parseFlatAttributeFilter(
  block: unknown,
  allowedKeys: ReadonlySet<string> = FLAT_FILTER_KEYS,
): AttributeFilterState {
  const record = flatAttributeBlockShape(block, allowedKeys);
  if (record == null || typeof record.field !== 'string') {
    throw new Error(`Not a flat attribute filter: ${JSON.stringify(block)}`);
  }
  const operator = flatUiOperator(record);
  return {
    field: record.field,
    operator,
    value: isUnaryFilterOperator(operator) || record.value == null ? null : String(record.value),
    value2: isTwoValueFilterOperator(operator) && record.value2 != null ? String(record.value2) : null,
  };
}

/**
 * The flat block for a row's state.
 *
 * `value` and `inverse` are always written, and `value2` only for an operator
 * that takes two values. That is what the wire's other producer writes, so a
 * condition saved from here is byte-identical to the same condition saved from
 * anywhere else — which is what keeps a save from rewriting rows the user
 * never touched.
 */
export function serializeFlatAttributeFilter(state: AttributeFilterState): FlatWireAttributeFilter {
  const negated = state.operator.startsWith('!');
  const wireOperator = negated ? state.operator.slice(1) : state.operator;

  const block: FlatWireAttributeFilter = {
    field: state.field.trim(),
    operator: wireOperator,
    value: isUnaryFilterOperator(state.operator) || state.value == null ? null : state.value.trim(),
    inverse: negated,
  };
  if (isTwoValueFilterOperator(state.operator)) {
    block.value2 = state.value2 == null ? null : state.value2.trim();
  }
  return block;
}

/**
 * Whether a relative timestamp value survives this wire's round trip
 * unchanged.
 *
 * The wire's other encoder floors a relative offset to whole days, so a
 * whole-day multiple is the only value that comes back as it went in. A
 * sub-day offset, or a non-canonical spelling like `'+86400'`, would be
 * silently rewritten; declining the claim leaves such a leaf on the
 * passthrough, which preserves the stored bytes instead. Absolute variants
 * carry their epoch verbatim and need no gate.
 */
export function hasRoundTrippableTimestampValue(block: WireAttributeBlock): boolean {
  if (timestampVariantOf(String(block.operator ?? '')) !== 'relative') {
    return true;
  }
  if (typeof block.value !== 'string') {
    return false;
  }
  const seconds = Number.parseInt(block.value);
  return String(seconds) === block.value && seconds % SECONDS_IN_DAY === 0;
}

/**
 * The block with a relative timestamp's absent day count read as zero days.
 *
 * A blank day count is a state the date editor itself writes — it stores no
 * value while the field is empty — and the wire's other reader takes that shape
 * to mean zero days, re-saving it as `'0'`. Normalizing before the whole-day
 * gate above therefore does two things at once: `'0'` passes the gate, so the
 * user's own saved row reloads as an editable row instead of an inert one, and
 * the re-save emits the same bytes the other producer would.
 *
 * Only an absent value is normalized. A blank string, or any other
 * non-canonical spelling, stays declined onto the passthrough: no editor writes
 * those, so preserving the stored bytes beats reproducing a rewrite.
 */
export function withZeroDayRelativeTimestamp(block: WireAttributeBlock): WireAttributeBlock {
  if (timestampVariantOf(String(block.operator ?? '')) !== 'relative' || block.value != null) {
    return block;
  }
  return { ...block, value: '0' };
}
